import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ApplicationIcon,
  Box,
  Button,
  Icon,
  Link as DSLink,
  Loader,
  Typography,
} from '@saviynt/design-system';

import { CF_NEU_BLD, CF_SEC_MED } from '../../constants';
import { useInvertedLocalize } from '../../hooks';
import { globalActions } from '../../store/globalSlice';
import { type AccessPathParams } from '../../types';

import './SodAccessPath.css';

const LADDER_LIMIT = 3;

interface AccessStepProps {
  title: string;
  subtitle: string;
}

/**
 * AccessStep component renders a single step in the access path with a title and subtitle.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - The title of the access step.
 * @param {string} props.subtitle - The subtitle or description of the access step.
 * @returns {ReactElement} The rendered AccessStep component.
 */
function AccessStep({ title, subtitle }: AccessStepProps): ReactElement {
  return (
    <Box tag='div' className='AccessStep-wrapper'>
      <ApplicationIcon logo='Entitlement' />
      <Box tag='div' className='AccessStep-content'>
        <Typography
          kind='body3'
          desktop='body3-d'
          mobile='body3-d'
          lineLimit={1}
          color={CF_SEC_MED}>
          {title}
        </Typography>
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          lineLimit={1}
          color={CF_NEU_BLD}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

interface SodAccessPathData {
  accessPath: string[];
  assignedEntitlementName: string;
  violatingEntitlementName: string;
}

interface PathItem {
  id: string;
  title: string;
  subtitle: string;
}

/**
 * SodAccessPath component displays a visual path of access with a collapsible middle section
 * when the number of items exceeds a certain threshold (3).
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.violationId - The ID of the violation.
 * @param {string} props.assignedEntitlementId - The ID of the assigned entitlement.
 * @param {string} props.violatingEntitlementId - The ID of the violating entitlement.
 * @returns {ReactElement} The rendered SodAccessPath component.
 */
function SodAccessPath({
  violationId,
  assignedEntitlementId,
  violatingEntitlementId,
}: AccessPathParams): ReactElement {
  const l = useInvertedLocalize();
  const [isExpanded, setIsExpanded] = useState(false);

  const [data, setData] = useState<SodAccessPathData | null>(null);
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [fetchAccessPath, { isLoading, isError, isFetching }] =
    globalActions.useLazyGetAccessPathQuery();

  const handleFetchAccessPath = useCallback(
    async (vId: string, aId: string, vEId: string) => {
      abortRef.current?.('New Search');

      try {
        const controller = fetchAccessPath({
          violationId: vId,
          assignedEntitlementId: aId,
          violatingEntitlementId: vEId,
        });

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch {
        throw Error('Failed Access Path API');
      }
    },
    [fetchAccessPath]
  );

  const triggerFetchAccessPath = useCallback(
    (vId: string, aId: string, vEId: string) => {
      handleFetchAccessPath(vId, aId, vEId)
        .then((response) => {
          const typedResponse = response as SodAccessPathData;

          setData(typedResponse);
        })
        .catch(() => {});
    },
    [handleFetchAccessPath]
  );

  useEffect(() => {
    triggerFetchAccessPath(
      violationId,
      assignedEntitlementId,
      violatingEntitlementId
    );
  }, [
    violationId,
    assignedEntitlementId,
    violatingEntitlementId,
    triggerFetchAccessPath,
  ]);

  if (isLoading || isFetching) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Loader kind='dots' format='inline' color='brand' />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Typography kind='body2'>
          {l('Error loading access path details, please try again.')}
        </Typography>
        <Button
          type='button'
          kind='outlined'
          isEnabled
          onClick={() => {
            triggerFetchAccessPath(
              violationId,
              assignedEntitlementId,
              violatingEntitlementId
            );
          }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (data === null) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Typography kind='body2'>{l('No data found!')}</Typography>
      </Box>
    );
  }

  const mapAccesPathData = (): PathItem[] => {
    const { accessPath, assignedEntitlementName, violatingEntitlementName } =
      data;

    const updatedAccessPath: string[] = [...accessPath];

    if (accessPath.length === 1) {
      const onlyOnePathItem = accessPath[0];

      const isOnlyOneItemEqualsToAssignedAndViolating =
        onlyOnePathItem === assignedEntitlementName &&
        onlyOnePathItem === violatingEntitlementName;

      if (isOnlyOneItemEqualsToAssignedAndViolating) {
        updatedAccessPath.push(onlyOnePathItem);
      }
    }

    const isAssginedEntitlementNotExistInAccessPath = !accessPath.includes(
      assignedEntitlementName
    );

    if (isAssginedEntitlementNotExistInAccessPath) {
      updatedAccessPath.push(assignedEntitlementName);
    }

    const isViolatingEntilmentNotExistInAccessPath = !accessPath.includes(
      violatingEntitlementName
    );

    if (isViolatingEntilmentNotExistInAccessPath) {
      updatedAccessPath.push(violatingEntitlementName);
    }

    const mappedAccessPath = updatedAccessPath.map((item, index) => {
      if (index === 0) {
        return {
          id: `'Assigned Entitlement-${item}`,
          title: l('Assigned Entitlement'),
          subtitle: item,
        };
      }

      if (index === updatedAccessPath.length - 1) {
        return {
          id: `'Violating Entitlement-${item}`,
          title: l('Violating Entitlement'),
          subtitle: item,
        };
      }

      return {
        id: `Entitlement-${item}`,
        title: l('Entitlement'),
        subtitle: item,
      };
    });

    return mappedAccessPath;
  };

  const pathItems: PathItem[] = mapAccesPathData();

  const itemsLength = pathItems.length;

  return (
    <Box tag='div' className='SodAccessPath-wrapper'>
      {pathItems.map((pathItem, index) => {
        const isNotLast = index !== itemsLength - 1;
        const isNoShow = Boolean(
          !isExpanded &&
            itemsLength > LADDER_LIMIT &&
            index >= 1 &&
            index < itemsLength - 2
        );
        const isExpandBtn = Boolean(
          !isExpanded && itemsLength > LADDER_LIMIT && index === 0
        );

        if (isNoShow) {
          return null;
        }

        return (
          <React.Fragment key={pathItem.id}>
            <AccessStep title={pathItem.title} subtitle={pathItem.subtitle} />
            {isExpandBtn && (
              <Box tag='div' className='AccessButton-wrapper'>
                <Icon
                  kind='Subtract'
                  size='medium'
                  rotate='90'
                  color='navy-500'
                />
                <DSLink
                  text={`+${itemsLength - LADDER_LIMIT}`}
                  kind='button'
                  onClick={() => {
                    setIsExpanded(true);
                  }}
                  suffixIconKind='ChevronDownSmall'
                />
              </Box>
            )}
            {isNotLast && (
              <Box tag='div' className='AccessIcon-wrapper'>
                <Icon kind='ArrowDown' color='navy-500' />
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

SodAccessPath.defaultProps = {
  pathItems: [],
};

export default memo(SodAccessPath);
